"use client";

import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useContactFormMutation } from "modules/shared/api/hooks/useContactFormMutation";
import Link from "next/link";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import background from "./assets/background.png";

const labelStyle = {
  sx: {
    color: "black",
    fontWeight: 600,
    fontSize: "16px",
    "& .MuiInputLabel-asterisk": {
      color: "red",
    },
  },
};

const contactSchema = yup.object({
  firstName: yup.string().required("First name is required"),
  lastName: yup.string().required("Last name is required"),
  email: yup
    .string()
    .email("Invalid email address")
    .required("Email is required"),
  company: yup.string(),
  topic: yup.string().required("Topic is required"),
  message: yup.string().required("Message is required"),
  confirm: yup.boolean().required("You must confirm the terms"),
});

export type ContactFormValues = yup.InferType<typeof contactSchema>;

const CONTACTS = [
  {
    title: "Marketing department",
    email: "marketing@prometeus.io",
  },
  {
    title: "Ecosystem and growth",
    email: "bd@prometeus.io",
  },
  {
    title: "General inquiries",
    email: "info@prometeus.io",
  },
];

export const Contacts = () => {
  const { mutate, isError, isSuccess } = useContactFormMutation();
  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitting, isSubmitted },
  } = useForm<ContactFormValues>({
    mode: "all",
    resolver: yupResolver(contactSchema),
  });

  const submit = (values: ContactFormValues) => {
    mutate(values);
  };

  return (
    <Stack
      gap={{ xs: "60px", sm: "100px" }}
      paddingBottom={{ xs: "90px", lg: "160px" }}
    >
      <Stack
        position="relative"
        flexDirection={{ xs: "column", lg: "row" }}
        minHeight="406px"
        justifyContent="space-around"
        alignItems={{ xs: "center", lg: "inherit" }}
        gap={{ xs: 11, lg: 0 }}
        padding={{ xs: 3, lg: 13 }}
      >
        <Stack height="406px" position="absolute" width="100%" top={0}>
          <img
            src={background.src}
            alt="background"
            width="100%"
            height="100%"
          />
        </Stack>
        <Typography
          zIndex={1}
          variant="h1"
          height={{ xs: "auto", lg: "306px" }}
          display="flex"
          alignItems="center"
        >
          Get in touch
        </Typography>
        <Stack
          sx={{
            height: "fit-content",
            alignSelf: { xs: "center", lg: "baseline" },
            maxWidth: "640px",
            width: "100%",
            background: "white",
            borderRadius: 5,
            padding: 5,
            zIndex: 1,
          }}
        >
          {isSubmitted || isError || isSuccess ? (
            <Stack gap={20}>
              <Stack gap={4}>
                <Typography variant="h3">Thanks reaching out to us!</Typography>
                <Typography
                  fontWeight={600}
                  variant="subtitle1"
                  color="text.secondary"
                >
                  {/* eslint-disable-next-line react/no-unescaped-entities */}
                  {isError ? (
                    <>
                      Something went wrong, please try again later or contact
                      <a href="mailto:prom.form@prometeus.io">
                        info@prometeus.io
                      </a>
                      directly
                    </>
                  ) : (
                    "We'll be in touch soon"
                  )}
                </Typography>
              </Stack>
              <Link passHref href="/">
                <Button variant="contained" color="primary">
                  Go to main page
                </Button>
              </Link>
            </Stack>
          ) : (
            <form onSubmit={handleSubmit(submit)}>
              <Stack sx={{ gap: 9, display: "flex", flexDirection: "column" }}>
                <Typography variant="subtitle1">Contact form</Typography>
                <Stack gap={11}>
                  <Stack
                    flexDirection={{ xs: "column", sm: "row" }}
                    width="100%"
                    gap={5}
                  >
                    <Controller
                      control={control}
                      name="firstName"
                      render={({ field }) => (
                        <TextField
                          InputLabelProps={labelStyle}
                          sx={{ width: "100%" }}
                          required
                          label="First name"
                          placeholder="John"
                          {...field}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="lastName"
                      render={({ field }) => (
                        <TextField
                          InputLabelProps={labelStyle}
                          inputProps={{ height: "40px" }}
                          required
                          sx={{ width: "100%" }}
                          label="Last name"
                          placeholder="Doe"
                          {...field}
                        />
                      )}
                    />
                  </Stack>
                  <Stack
                    flexDirection={{ xs: "column", sm: "row" }}
                    width="100%"
                    gap={5}
                  >
                    <Controller
                      control={control}
                      name="email"
                      render={({ field }) => (
                        <TextField
                          InputLabelProps={labelStyle}
                          required
                          sx={{ width: "100%" }}
                          inputProps={{ height: "40px" }}
                          label="Email address"
                          placeholder="John@gmail.com"
                          {...field}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="company"
                      render={({ field }) => (
                        <TextField
                          InputLabelProps={labelStyle}
                          sx={{ width: "100%" }}
                          inputProps={{ height: "40px" }}
                          label="Company"
                          placeholder="Company name"
                          {...field}
                        />
                      )}
                    />
                  </Stack>
                  <Controller
                    name="topic"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        placeholder="Select topic"
                        inputProps={{ height: "40px" }}
                        InputLabelProps={labelStyle}
                        required
                        label="Topic"
                        select
                        {...field}
                      >
                        <MenuItem key="Currency" value="currency">
                          Currency
                        </MenuItem>
                        <MenuItem key="Api" value="api">
                          Api
                        </MenuItem>
                      </TextField>
                    )}
                  />
                  <Stack gap={7}>
                    <Controller
                      control={control}
                      name="message"
                      render={({ field }) => (
                        <TextField
                          inputProps={{ height: "40px" }}
                          InputLabelProps={labelStyle}
                          required
                          label="Message"
                          placeholder="Your message"
                          {...field}
                        />
                      )}
                    />
                    <FormControlLabel
                      control={
                        <Controller
                          name="confirm"
                          control={control}
                          render={({ field }) => {
                            return (
                              <Checkbox
                                required
                                sx={{ color: "text.primary" }}
                                {...field}
                              />
                            );
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2" color="text.secondary">
                          I confirm that I have read and accept the Terms of Use
                          and consent to receiving marketing materials from Prom
                        </Typography>
                      }
                    />
                  </Stack>
                </Stack>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={!isValid || isSubmitting || !confirm}
                >
                  Submit
                </Button>
              </Stack>
            </form>
          )}
        </Stack>
      </Stack>
      <Stack
        flexDirection={{ xs: "column", lg: "row" }}
        alignItems={{ xs: "center", lg: "initial" }}
        justifyContent="space-around"
        gap={10}
        padding={3}
      >
        <Typography variant="h2" maxWidth="325px" textAlign="center">
          Don’t have time to wait? Reach out to us directly!
        </Typography>
        <Stack
          gap={5}
          flexDirection={{ xs: "column", md: "row" }}
          width={{ xs: "100%", md: "fit-content" }}
          alignItems="center"
        >
          {CONTACTS.map(({ title, email }) => {
            return (
              <Link key={email} passHref href={`mailto:${email}`}>
                <Stack
                  width={{ xs: "100%", md: "250px" }}
                  height="128px"
                  border="1px solid #D0D0D0"
                  borderRadius="12px"
                  alignItems="center"
                  justifyContent="center"
                  gap={1}
                  sx={{ cursor: "pointer" }}
                >
                  <Typography fontWeight={600}>{title}</Typography>
                  <Typography>{email}</Typography>
                </Stack>
              </Link>
            );
          })}
        </Stack>
      </Stack>
    </Stack>
  );
};
